import { Box, Card, Grid, Typography, useTheme, alpha, Chip } from '@mui/material';
import { FC, useMemo } from 'react';
import { TFunction } from 'i18next';
import { H3, Small } from 'components/Typography';

import BeachAccessIcon from '@mui/icons-material/BeachAccess';
import EventBusyIcon from '@mui/icons-material/EventBusy';
import WorkOffIcon from '@mui/icons-material/WorkOff';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import EventIcon from '@mui/icons-material/Event';

interface EventsOverviewProps {
    eventCounts: Record<string, number>;
    eventColors: Record<string, string>;
    workDays: number;
    totalDays: number;
    t: TFunction;
}

// Map event types to icons
const typeIcons: Record<string, React.ElementType> = {
    VACATION: BeachAccessIcon,
    ILL: LocalHospitalIcon,
    DAY_OFF: WorkOffIcon,
    UNPAID_LEAVE: EventBusyIcon,
    HALF_DAY: EventIcon,
};

interface StatItemProps {
    title: string;
    count: number;
    color: string;
    Icon?: React.ElementType;
}

const StatItem: FC<StatItemProps> = ({ title, count, color, Icon }) => {
    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                py: 1.5,
                px: 2,
                borderRadius: 1,
                backgroundColor: alpha(color, 0.08),
                minWidth: 0,
            }}
        >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0, flex: 1 }}>
                {Icon && (
                    <Icon sx={{ color, fontSize: 20, flexShrink: 0 }} />
                )}
                <Typography
                    variant="body2"
                    sx={{
                        fontWeight: 500,
                        color: 'text.primary',
                        wordBreak: 'break-word',
                    }}
                >
                    {title}
                </Typography>
            </Box>
            <Chip
                label={count}
                size="small"
                sx={{
                    ml: 1,
                    fontWeight: 700,
                    backgroundColor: color,
                    color: '#fff',
                    minWidth: 32,
                    '& .MuiChip-label': {
                        px: 1,
                    },
                }}
            />
        </Box>
    );
};

const EmployeeEventsOverview: FC<EventsOverviewProps> = ({
    eventCounts,
    eventColors,
    workDays,
    totalDays,
    t,
}) => {
    const theme = useTheme();

    // Translation map for standard types
    const typeTranslations: Record<string, string> = useMemo(
        () => ({
            VACATION: t('employees.report.stats.holidays'),
            ILL: t('employees.report.stats.ill'),
            DAY_OFF: t('employees.calendar.types.day_off'),
            UNPAID_LEAVE: t('employees.report.stats.unpaidLeave'),
            HALF_DAY: t('employees.calendar.types.half_day'),
        }),
        [t]
    );

    // Build stats list from eventCounts
    const statsList = useMemo(() => {
        const list: Array<{ key: string; title: string; count: number; color: string; Icon?: React.ElementType }> = [];

        // Standard types first
        const standardOrder = ['DAY_OFF', 'VACATION', 'ILL', 'UNPAID_LEAVE', 'HALF_DAY'];
        standardOrder.forEach((type) => {
            if (eventCounts[type] !== undefined && eventCounts[type] > 0) {
                list.push({
                    key: type,
                    title: typeTranslations[type] || type,
                    count: eventCounts[type],
                    color: eventColors[type] || '#607D8B',
                    Icon: typeIcons[type] || EventIcon,
                });
            }
        });

        // Custom types
        Object.entries(eventCounts).forEach(([type, count]) => {
            if (!standardOrder.includes(type) && count > 0) {
                list.push({
                    key: type,
                    title: type, // Custom type name is already user-defined
                    count,
                    color: eventColors[type] || '#607D8B',
                    Icon: EventIcon,
                });
            }
        });

        return list;
    }, [eventCounts, eventColors, typeTranslations]);

    if (statsList.length === 0) {
        return (
            <Card sx={{ p: 3, textAlign: 'center' }}>
                <Typography color="text.secondary">
                    {t('employees.report.stats.noEvents')}
                </Typography>
            </Card>
        );
    }

    return (
        <Card sx={{ p: 2 }}>
            <Grid container spacing={1.5}>
                {statsList.map((stat) => (
                    <Grid item xs={12} sm={6} md={4} key={stat.key}>
                        <StatItem
                            title={stat.title}
                            count={stat.count}
                            color={stat.color}
                            Icon={stat.Icon}
                        />
                    </Grid>
                ))}
            </Grid>

            {/* Summary row */}
            <Box
                sx={{
                    mt: 2,
                    pt: 2,
                    borderTop: `1px solid ${theme.palette.divider}`,
                    display: 'flex',
                    justifyContent: 'space-around',
                    flexWrap: 'wrap',
                    gap: 2,
                }}
            >
                <Box sx={{ textAlign: 'center' }}>
                    <Small color="text.secondary">{t('employees.report.stats.workDays')}</Small>
                    <H3 color="text.primary">{workDays}</H3>
                </Box>
                <Box sx={{ textAlign: 'center' }}>
                    <Small color="text.secondary">{t('employees.report.stats.totalDays')}</Small>
                    <H3 color="text.primary">{totalDays}</H3>
                </Box>
                <Box sx={{ textAlign: 'center' }}>
                    <Small color="text.secondary">{t('employees.report.stats.absences')}</Small>
                    <H3 color="text.primary">{totalDays - workDays}</H3>
                </Box>
            </Box>
        </Card>
    );
};

export default EmployeeEventsOverview;
